import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login for 401 on non-auth endpoints
    // Don't redirect if we're already on an auth endpoint (login, register, pattern verify, etc.)
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  // Check available auth methods
  getAuthMethods: (phone) => api.post('/auth/methods', { phone }),

  // Register new user (no OTP needed)
  registerUser: (phone) => api.post('/auth/register', { phone }),

  // Direct login for existing users (no OTP)
  directLogin: (phone) => api.post('/auth/login', { phone }),

  // OTP auth
  sendOTP: (phone) => api.post('/auth/send-otp', { phone }),
  verifyOTP: (phone, otp) => api.post('/auth/verify-otp', { phone, otp }),

  // Pattern auth
  setupPattern: (pattern) => api.post('/auth/pattern/setup', { pattern }),
  verifyPattern: (phone, pattern) => api.post('/auth/pattern/verify', { phone, pattern }),
  disablePattern: () => api.post('/auth/pattern/disable'),

  // Passkey auth
  getPasskeyRegisterChallenge: () => api.post('/auth/passkey/register/challenge'),
  registerPasskey: (data) => api.post('/auth/passkey/register', data),
  getPasskeyChallenge: (phone) => api.post('/auth/passkey/challenge', { phone }),
  verifyPasskey: (data) => api.post('/auth/passkey/verify', data),
  removePasskey: (credentialId) => api.delete(`/auth/passkey/${credentialId}`),

  // Profile
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  setPreferredAuthMethod: (method) => api.put('/auth/preferred-method', { method })
};

// Location APIs
export const locationAPI = {
  getAll: () => api.get('/locations'),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  setDefault: (id) => api.put(`/locations/${id}/default`)
};

// Order APIs
export const orderAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  shareInstructions: (id) => api.post(`/orders/${id}/share-instructions`),
  getStats: () => api.get('/orders/stats'),
  parseSMS: (id, smsText) => api.post(`/orders/${id}/parse-sms`, { smsText }),
  parseSMSOnly: (smsText) => api.post('/orders/parse-sms', { smsText })
};

// Gate Pass APIs
export const gatepassAPI = {
  generate: (orderId) => api.post('/gatepass/generate', { orderId }),
  getByOrder: (orderId) => api.get(`/gatepass/order/${orderId}`),
  notifySecurity: (orderId) => api.post('/gatepass/notify-security', { orderId }),
  getActive: () => api.get('/gatepass/active')
};

// Upload APIs
export const uploadAPI = {
  uploadPhoto: (file, caption, type) => {
    const formData = new FormData();
    formData.append('photo', file);
    if (caption) formData.append('caption', caption);
    if (type) formData.append('type', type);
    return api.post('/upload/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadToLocation: (locationId, file, caption, type) => {
    const formData = new FormData();
    formData.append('photo', file);
    if (caption) formData.append('caption', caption);
    if (type) formData.append('type', type);
    return api.post(`/upload/location/${locationId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteFromLocation: (locationId, photoIndex) =>
    api.delete(`/upload/location/${locationId}/photo/${photoIndex}`),
  uploadAudio: (formData) =>
    api.post('/upload/audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};

export default api;
